import { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../contexts/UserContext";
import { PregnancyData, PregnancyLog, PregnancyWeekInfo, EndPregnancyReason } from "../types";
import {
  calculateDueDate,
  getPregnancyWeekInfo,
} from "../utils/PregnancyCalculations";

export interface UsePregnancyDataResult {
  pregnancy: PregnancyData | null;
  pregnancyLogs: PregnancyLog[];
  weekInfo: PregnancyWeekInfo | null;
  isPregnant: boolean;
  loading: boolean;
  activatePregnancy: (opts: {
    lastPeriodDate: string;
    positiveTestDate: string;
    dueDate?: string;
  }) => Promise<void>;
  deactivatePregnancy: (reason?: EndPregnancyReason, notes?: string, week?: number) => Promise<void>;
  addLog: (
    log: Omit<PregnancyLog, "id" | "userId" | "pregnancyId" | "createdAt">
  ) => Promise<void>;
  updateLog: (logId: string, updates: Partial<PregnancyLog>) => Promise<void>;
  deleteLog: (logId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const usePregnancyData = (): UsePregnancyDataResult => {
  const { userDetails } = useUser()!;
  const uid = userDetails?.user?.uid;

  const [pregnancy, setPregnancy] = useState<PregnancyData | null>(null);
  const [pregnancyLogs, setPregnancyLogs] = useState<PregnancyLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!uid) {
      setLoading(false);
      return;
    }

    try {
      // Fetch active pregnancy
      const pregRef = collection(db, "pregnancyData");
      const pregQ = query(
        pregRef,
        where("userId", "==", uid),
        where("status", "==", "active")
      );
      const pregSnap = await getDocs(pregQ);

      if (pregSnap.empty) {
        setPregnancy(null);
        setPregnancyLogs([]);
        setLoading(false);
        return;
      }

      const pregData = {
        ...pregSnap.docs[0].data(),
        id: pregSnap.docs[0].id,
      } as PregnancyData;
      setPregnancy(pregData);

      // Fetch pregnancy logs
      const logsRef = collection(db, "pregnancyLog");
      const logsQ = query(
        logsRef,
        where("userId", "==", uid),
        where("pregnancyId", "==", pregData.id)
      );
      const logsSnap = await getDocs(logsQ);
      const logs = logsSnap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      })) as PregnancyLog[];

      logs.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      setPregnancyLogs(logs);
    } catch (error) {
      console.error("[PregnancyData] Error fetching:", error);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isPregnant = !!pregnancy && pregnancy.status === "active";

  const weekInfo = useMemo(
    () =>
      pregnancy
        ? getPregnancyWeekInfo(pregnancy.dueDate, pregnancy.lastPeriodDate)
        : null,
    [pregnancy]
  );

  const activatePregnancy = useCallback(
    async (opts: {
      lastPeriodDate: string;
      positiveTestDate: string;
      dueDate?: string;
    }) => {
      if (!uid) return;
      const now = new Date().toISOString();
      const dueDate = opts.dueDate || calculateDueDate(opts.lastPeriodDate);

      const newPregnancy = {
        userId: uid,
        status: "active" as const,
        dueDate,
        lastPeriodDate: opts.lastPeriodDate,
        positiveTestDate: opts.positiveTestDate,
        activatedAt: now,
        endedAt: null,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(
        collection(db, "pregnancyData"),
        newPregnancy
      );
      setPregnancy({ ...newPregnancy, id: docRef.id });
      setPregnancyLogs([]);
    },
    [uid]
  );

  const deactivatePregnancy = useCallback(async (reason?: EndPregnancyReason, notes?: string, week?: number) => {
    if (!pregnancy) return;
    const now = new Date().toISOString();
    const docRef = doc(db, "pregnancyData", pregnancy.id);
    const isLoss = reason && reason !== "delivered";
    const status = isLoss ? "loss" : "ended";
    await updateDoc(docRef, {
      status,
      endedAt: now,
      updatedAt: now,
      ...(reason && { endReason: reason }),
      ...(notes && { endNotes: notes }),
      ...(week !== undefined && { endWeek: week }),
    });
    setPregnancy(null);
    setPregnancyLogs([]);
  }, [pregnancy]);

  const addLog = useCallback(
    async (
      log: Omit<PregnancyLog, "id" | "userId" | "pregnancyId" | "createdAt">
    ) => {
      if (!uid || !pregnancy) return;
      const newLog = {
        ...log,
        userId: uid,
        pregnancyId: pregnancy.id,
        createdAt: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, "pregnancyLog"), newLog);
      const logWithId = { ...newLog, id: docRef.id } as PregnancyLog;
      setPregnancyLogs((prev) => [logWithId, ...prev]);
    },
    [uid, pregnancy]
  );

  const updateLog = useCallback(
    async (logId: string, updates: Partial<PregnancyLog>) => {
      const docRef = doc(db, "pregnancyLog", logId);
      const { id: _, ...safeUpdates } = updates as Record<string, unknown>;
      await updateDoc(docRef, safeUpdates);
      setPregnancyLogs((prev) =>
        prev.map((l) => (l.id === logId ? { ...l, ...updates } : l))
      );
    },
    []
  );

  const deleteLog = useCallback(async (logId: string) => {
    const docRef = doc(db, "pregnancyLog", logId);
    await deleteDoc(docRef);
    setPregnancyLogs((prev) => prev.filter((l) => l.id !== logId));
  }, []);

  return {
    pregnancy,
    pregnancyLogs,
    weekInfo,
    isPregnant,
    loading,
    activatePregnancy,
    deactivatePregnancy,
    addLog,
    updateLog,
    deleteLog,
    refetch: fetchData,
  };
};

export default usePregnancyData;
